import { useQuery } from '@apollo/client';
import { AccountCreditCardModal } from '@retainerclub/common';
import { GET_ACCOUNT_CREDIT_CARDS_CHECK } from '@retainerclub/customer-api';
import { useAccount } from '@retainerclub/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import DashboardBanner from './DashboardBanner';
import useBannerDismissal from './useBannerDismissal';

function DashboardBanners() {
  const { account } = useAccount();
  const { dismissBanner, isBannerDismissed } = useBannerDismissal(
    'dashboard-banners-dismissed',
  );
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleNavigateToPaymentMethods = () => {
    navigate('/payment-methods');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Query for credit card status from the server
  const { data, loading, error } = useQuery(GET_ACCOUNT_CREDIT_CARDS_CHECK, {
    skip: !account?.id, // Skip query if no account
    fetchPolicy: 'cache-and-network', // Always get fresh data
  });

  // Auto-open modal when credit card issues exist
  /*
  useEffect(() => {
    if (data?.accountCreditCardsCheck) {
      const { hasCards, defaultCardExpired, defaultCardExpiring } =
        data.accountCreditCardsCheck;
      if (!hasCards || defaultCardExpired || defaultCardExpiring) {
        setIsModalOpen(true);
      }
    }
  }, [data?.accountCreditCardsCheck]);
  */

  // Don't render anything if still loading or if there's an error
  const hasValidData = data?.accountCreditCardsCheck;
  if (loading || error || !hasValidData) {
    return null;
  }

  const { hasCards, defaultCardExpired, defaultCardExpiring } =
    data.accountCreditCardsCheck;

  // Don't render anything if no banners should be shown
  const shouldShowBanners =
    !hasCards || defaultCardExpired || defaultCardExpiring;
  if (!shouldShowBanners) {
    return null;
  }

  return (
    <div className="mb-6 space-y-4">
      {/* No Credit Card Banner */}
      {!(hasCards || isBannerDismissed('no-credit-card')) && (
        <DashboardBanner
          type="warning"
          title="Payment Method Needed"
          message="Add a payment method to ensure access to all account features and benefits."
          actionText="Add Payment Method"
          onAction={handleNavigateToPaymentMethods}
          onDismiss={() => dismissBanner('no-credit-card')}
        />
      )}

      {/* Expired Credit Card Banner */}
      {defaultCardExpired && !isBannerDismissed('expired-credit-card') && (
        <DashboardBanner
          type="danger"
          title="Payment Method Issue"
          message="Your default credit card has expired. Please update your payment method to avoid service interruptions."
          actionText="Update Payment Method"
          onAction={handleNavigateToPaymentMethods}
          onDismiss={() => dismissBanner('expired-credit-card')}
        />
      )}

      {/* Expiring Credit Card Banner */}
      {defaultCardExpiring && !isBannerDismissed('expiring-credit-card') && (
        <DashboardBanner
          type="warning"
          title="Payment Method Expiring Soon"
          message="Your default credit card will expire soon. Please update your payment method to avoid service interruptions."
          actionText="Update Payment Method"
          onAction={handleNavigateToPaymentMethods}
          onDismiss={() => dismissBanner('expiring-credit-card')}
        />
      )}

      {/* Auto-opening Credit Card Modal */}
      <AccountCreditCardModal
        open={isModalOpen}
        handleCloseModal={handleCloseModal}
        accountId={account?.id}
        title="Please add a Credit Card"
        hideDefaultCardCheckbox
      />
    </div>
  );
}

export default DashboardBanners;
